// ============================================================
// THE STATES A PLATE DRAWS (plan body-map S1; the bench's conditions, ruled
// 2026-10-08): a combatant's condition codes read as one record - the flags,
// Haywire's severity, the hallucination's band, Fatigue's level and how much
// blood is lost. Death ends every other condition: a dead plate draws only
// its death. A vehicle's own (plan S2) ride on the same record: its weapon,
// engine, hull, crew and systems; and Explodes ends the rest as death does.
//
// Pure. Zero React dependency.
// ============================================================

import { condType } from '../conditions';
import { bandOf, HALLUCINATION_BANDS, parseHallucination } from '../hallucinationCode';

/**
 * Nothing on the plate: every state off. One table for the record's shape, its defaults and its ORDER - written in
 * the plate's stack order, outside in, which is also the order `activeStates` names them.
 */
const NONE = {
  dead: false,
  dying: false,
  /** Haywire's severity, 0 (none) to 4 (Total Shutdown). */
  haywire: 0,
  /** The hallucination's band, 0 (none), 1 (faint) to 6 (lost). */
  halluc: 0,
  paralysed: false,
  unconscious: false,
  helpless: false,
  unaware: false,
  /** Fatigue's level, 0 to 4 (the panting deepens no further). */
  fatigue: 0,
  pinned: false,
  stun: false,
  fire: false,
  /** 0, 1 (Blood Loss) or 2 (Severe). */
  bloodLoss: 0,
  mute: false,
  blind: false,
  deaf: false,
  snared: false,
  immob: false,
  prone: false,
  /** A vehicle's: Explodes, System Disabled, Weapon Destroyed, Engine Damaged, Hull Breach, Crew Hit. */
  explodes: false,
  disabled: false,
  wepDest: false,
  engDmg: false,
  hullBr: false,
  crewHit: false,
};

export type PlateState = typeof NONE;

const ORDER = Object.keys(NONE) as (keyof PlateState)[];

/** The flags, by the condition type that sets them. */
const FLAGS: Record<string, keyof PlateState> = {
  Dy: 'dying',
  Pa: 'paralysed',
  U: 'unconscious',
  Hpl: 'helpless',
  Unw: 'unaware',
  Pn: 'pinned',
  S: 'stun',
  Fi: 'fire',
  NS: 'mute',
  B: 'blind',
  D: 'deaf',
  Sn: 'snared',
  Immob: 'immob',
  P: 'prone',
  WepDest: 'wepDest',
  EngDmg: 'engDmg',
  HullBr: 'hullBr',
  CrewHit: 'crewHit',
  Disabled: 'disabled',
};

/** The number a counted code carries (`Hw3`, `F2`), or 0. */
const countOf = (code: string) => parseInt(code.replace(/^[A-Za-z]+/, ''), 10) || 0;

const hallucBand = (code: string) => {
  const h = parseHallucination(code);
  return h ? HALLUCINATION_BANDS.indexOf(bandOf(h.intensity)) + 1 : 0;
};

/** The counted states: what each code's number (or severity) makes of its key, the worst one held. */
const COUNTED: Record<string, [key: 'haywire' | 'halluc' | 'fatigue' | 'bloodLoss', read: (code: string) => number]> = {
  Hw: ['haywire', (c) => Math.min(4, countOf(c))],
  Hl: ['halluc', hallucBand],
  F: ['fatigue', (c) => Math.min(4, countOf(c))],
  BL: ['bloodLoss', () => 1],
  BLS: ['bloodLoss', () => 2],
};

/** What one code adds to the record. */
function readCode(s: PlateState, code: string, type: string): void {
  const flag = FLAGS[type];
  if (flag) (s as unknown as Record<string, boolean>)[flag] = true;
  const counted = COUNTED[type];
  if (counted) s[counted[0]] = Math.max(s[counted[0]], counted[1](code));
}

/** The plate's state from a combatant's conditions. Death, or a vehicle exploding, ends every other condition. */
export function plateState(conditions: readonly string[]): PlateState {
  const types = conditions.map((c) => [c, condType(c)] as const);
  // Explodes first: every exploding vehicle critical applies Explodes AND K together, and the blast is the one to show.
  if (types.some(([, t]) => t === 'Explodes')) return { ...NONE, explodes: true };
  if (types.some(([, t]) => t === 'K')) return { ...NONE, dead: true };
  const s = { ...NONE };
  for (const [code, type] of types) readCode(s, code, type);
  return s;
}

/** The states in play, named in the stack order (the svg's `data-states`). */
export function activeStates(s: PlateState): string[] {
  return ORDER.filter((k) => s[k]);
}

/**
 * Whether the plate's clock must run: the filters and the moving bands it animates (Haywire, the hallucination,
 * Stunned's ghosts, the fire's heat, the voice, the calm glow, the draining blood). Everything else is CSS.
 */
const CLOCKED = ['haywire', 'halluc', 'stun', 'fire', 'mute', 'unaware', 'bloodLoss'] as const;
export const needsClock = (s: PlateState) => CLOCKED.some((k) => s[k]);
